import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const SECTIONS = [
  {
    title: '1. What the Sign-Up Bonus is',
    body: [
      'The Sign-Up Bonus is a promotional wallet credit that taskeri® may offer to newly registered clients and heroes while a bonus campaign is running in their city.',
      'It is a separate, standalone promotion. It is not a referral bonus, is held as a separate balance, and is recorded separately in your transaction history. Referral bonuses are governed by their own terms.',
    ],
  },
  {
    title: '2. Eligibility',
    body: [
      'You must be a newly registered taskeri® user, acting as either a client or a hero.',
      'An active campaign must cover the city saved on your profile at the time your address is saved. Some campaigns run nationwide; others target specific cities only.',
      'If no campaign is active for your city when you register, no bonus is issued. A campaign starting later does not apply retroactively.',
      'You must acknowledge these terms before the bonus is credited.',
    ],
  },
  {
    title: '3. One bonus per person',
    body: [
      'Only one Sign-Up Bonus may be issued per person. We enforce this using your email address and your phone number.',
      'Creating additional accounts, or using alternate email addresses or phone numbers to claim more than one bonus, is not permitted.',
    ],
  },
  {
    title: '4. No cash value',
    body: [
      'The Sign-Up Bonus has no cash value. It cannot be withdrawn, transferred, exchanged for cash, or paid out to a bank account.',
      'It is never included in hero payout calculations and does not form part of any amount payable to you as a hero.',
      'It can only be applied toward posting jobs on taskeri® and the associated platform fees.',
    ],
  },
  {
    title: '5. How and when it applies',
    body: [
      'The bonus is applied automatically at checkout when you post a qualifying job.',
      'Only jobs with a total value of $50 or more qualify.',
      'If your bonus is smaller than the job total, the bonus is applied first and the remainder is charged to your wallet balance or payment card.',
      'A single job may use either a Sign-Up Bonus or a referral credit, but not both. Where you hold both, the Sign-Up Bonus is used first because it expires.',
    ],
  },
  {
    title: '6. Expiry',
    body: [
      'The bonus expires 90 days after it lands in your wallet. Once it expires, any unused balance is removed and cannot be reinstated.',
      'We send reminder emails 30 days and 7 days before expiry, showing your remaining balance and expiry date.',
      'Any balance remaining at expiry is removed and cannot be reinstated or extended.',
    ],
  },
  {
    title: '7. Campaign changes',
    body: [
      'Campaigns have a maximum total budget. A campaign pauses automatically once its budget is exhausted, and bonuses are no longer issued under it.',
      'taskeri® may start, pause, amend or end any campaign at any time. Bonuses already credited keep their original expiry date.',
    ],
  },
  {
    title: '8. Abuse',
    body: [
      'taskeri® reserves the right to cancel, revoke or reclaim a Sign-Up Bonus, in whole or in part, where we detect or reasonably suspect abuse.',
      'This includes duplicate or fraudulent registrations, falsified city or contact details, coordinated or artificial job postings created primarily to extract bonus value, cancelling jobs after applying the bonus, and any attempt to convert the bonus to cash.',
      'Where abuse is detected we may also suspend or close the accounts involved.',
    ],
  },
  {
    title: '9. General',
    body: [
      'The bonus is offered at taskeri®\u2019s discretion and is not an entitlement.',
      'These terms operate alongside the taskeri® Terms of Service. Where they conflict on the subject of the Sign-Up Bonus, these terms apply.',
    ],
  },
];

const SignUpBonusTerms = () => (
  <div className="sbt-page" data-testid="signup-bonus-terms-page">
    <div className="sbt-container">
      <Link to="/" className="sbt-back" data-testid="signup-bonus-terms-back">
        <ArrowLeft size={16} /> Back
      </Link>

      <h1 className="sbt-title">Sign-Up Bonus Terms &amp; Conditions</h1>
      <p className="sbt-lede">
        These terms govern the taskeri® Sign-Up Bonus campaign. They are separate from the taskeri®
        referral programme, which has its own terms and its own balance.
      </p>

      <div className="sbt-highlights" data-testid="signup-bonus-key-terms">
        <div><strong>No cash value</strong><span>Cannot be withdrawn or paid out</span></div>
        <div><strong>$50 minimum</strong><span>Applies to qualifying jobs only</span></div>
        <div><strong>90 days</strong><span>From the day the bonus lands in your wallet</span></div>
        <div><strong>One per person</strong><span>Verified by email and phone</span></div>
      </div>

      {SECTIONS.map((s) => (
        <section key={s.title} className="sbt-section">
          <h2>{s.title}</h2>
          {s.body.map((p) => <p key={p.slice(0, 40)}>{p}</p>)}
        </section>
      ))}

      <p className="sbt-updated">Last updated: June 2026</p>
    </div>

    <style>{`
      .sbt-page { min-height: 100vh; background: #f8fafc; padding: 32px 16px 64px; }
      .sbt-container { max-width: 760px; margin: 0 auto; background: #fff; border-radius: 16px;
        padding: 36px 32px; border: 1px solid #e2e8f0; }
      .sbt-back { display: inline-flex; align-items: center; gap: 6px; color: #6b7280;
        text-decoration: none; font-size: 0.875rem; margin-bottom: 20px; }
      .sbt-back:hover { color: #7c3aed; }
      .sbt-title { font-size: 1.75rem; line-height: 1.2; color: #0f172a; margin: 0 0 10px; letter-spacing: -0.02em; }
      .sbt-lede { color: #475569; font-size: 0.9375rem; line-height: 1.7; margin: 0 0 24px; }
      .sbt-highlights { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin: 0 0 32px; }
      .sbt-highlights div { background: #f5f3ff; border: 1px solid #ddd6fe; border-radius: 12px; padding: 14px 16px; }
      .sbt-highlights strong { display: block; color: #5b21b6; font-size: 0.9375rem; margin-bottom: 2px; }
      .sbt-highlights span { color: #6b7280; font-size: 0.8125rem; }
      .sbt-section { margin: 0 0 26px; }
      .sbt-section h2 { font-size: 1.0625rem; color: #0f172a; margin: 0 0 8px; }
      .sbt-section p { color: #475569; font-size: 0.9375rem; line-height: 1.75; margin: 0 0 10px; }
      .sbt-updated { color: #94a3b8; font-size: 0.8125rem; margin: 32px 0 0; }
      @media (max-width: 600px) {
        .sbt-container { padding: 24px 18px; }
        .sbt-highlights { grid-template-columns: 1fr; }
        .sbt-title { font-size: 1.5rem; }
      }
    `}</style>
  </div>
);

export default SignUpBonusTerms;
